import * as React from 'react'

import type { PlaylistId } from 'types/playlist'

import { ContextMenuTarget } from 'components/ds'

import { useExplorerContextMenusEnabled } from 'hooks/use-explorer-context-menus-enabled'

import PlaylistMenuBody from '../playlist-menu-body'
import { usePlaylistSettingsAwait } from '../use-playlist-settings-await'

type Props = {
  children: React.ReactNode
  playlist: { playlist_id: PlaylistId } | null | undefined
  hideShare?: boolean
}

/**
 * Opens the playlist's three-dot menu on right-click anywhere in `children`.
 *
 * The menu acts on this playlist alone, matching the three-dot button, so a
 * right-click deliberately leaves the current multi-selection untouched.
 */
const PlaylistContextMenu = ({
  children,
  playlist,
  hideShare,
}: Props): React.ReactNode => {
  const contextMenusEnabled = useExplorerContextMenusEnabled()

  // Skip usePlaylistSettingsAwait's selector/effect entirely while the flag
  // is off, rather than calling it on every row and disabling after.
  if (!contextMenusEnabled || !playlist) return children

  return (
    <PlaylistContextMenuContent playlist={playlist} hideShare={hideShare}>
      {children}
    </PlaylistContextMenuContent>
  )
}

const PlaylistContextMenuContent = ({
  children,
  playlist,
  hideShare,
}: {
  children: React.ReactNode
  playlist: { playlist_id: PlaylistId }
  hideShare?: boolean
}): React.ReactNode => {
  const onPlaylistSettingsAwait = usePlaylistSettingsAwait(playlist)

  return (
    <ContextMenuTarget
      menu={
        <PlaylistMenuBody
          playlist={playlist}
          hideShare={hideShare}
          onPlaylistSettingsAwait={onPlaylistSettingsAwait}
        />
      }
    >
      {children}
    </ContextMenuTarget>
  )
}

export default PlaylistContextMenu
